import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/feature/SEOSchema";
import { categoryList } from "@/mocks/siteConfig";
import { getArticlesByCategory, type Article } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedSlug, setSelectedSlug] = useState(slug || "tutorial");
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  const currentCategory = useMemo(
    () => categoryList.find((c) => c.slug === selectedSlug),
    [selectedSlug]
  );

  useEffect(() => {
    if (slug) {
      setSelectedSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(getArticlesByCategory(selectedSlug));
  }, [selectedSlug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedSlug]);

  const handleCategoryChange = (newSlug: string) => {
    setSelectedSlug(newSlug);
    window.history.replaceState(null, "", `/category/${newSlug}`);
  };

  return (
    <>
      <SEOSchema
        type="category"
        categoryName={currentCategory?.name}
        categorySlug={selectedSlug}
      />
      <Navbar />
      <main className="min-h-screen pt-24 pb-20 bg-white">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <Breadcrumb
            items={[
              { name: "首页", path: "/" },
              { name: "内容分类" },
              { name: currentCategory?.name || "" },
            ]}
          />

          <div className="mt-8 mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              {currentCategory?.name}
            </h1>
            <p className="text-foreground-500 leading-relaxed">
              {currentCategory?.description}
            </p>
          </div>

          <div className="mb-10 flex items-center gap-2 flex-wrap">
            {categoryList.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedSlug === cat.slug
                    ? "bg-foreground-950 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group rounded-2xl border border-foreground-100 bg-white overflow-hidden hover:border-foreground-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - mogu`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {article.readTime} 分钟阅读
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {article.date}
                      </span>
                    </div>
                    <h2 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-foreground-700 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl" />
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}